import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';
import RootRef from '@material-ui/core/RootRef';

import { Field, reduxForm, SubmissionError } from 'redux-form';

import { UsaStates } from 'usa-states';

import TextField, { TextFieldAdornmentText } from 'components/fields/TextField';
import AutosuggestField from 'components/fields/AutosuggestField';
import SwitchField from 'components/fields/SwitchField';
import CheckboxField from 'components/fields/CheckboxField';
import FormError from 'components/common/FormError';
import IconInfo from 'components/icons/IconInfo';
import IconSMS from 'components/icons/IconSMS';
import IconEmail from 'components/icons/IconEmail';
import Tooltip from 'components/common/Tooltip';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';
import objectDiff from 'utils/objectDiff';

import { updatePatient } from 'redux/modules/patients';
import { requestFetchPlacesAutocomplete } from 'redux/modules/placesAutocomplete';

import primaryLanguages from 'config/primaryLanguages';

const usaStates = new UsaStates();

const styles = theme => ({
  gridSeparator: {
    marginTop: 24,
    paddingTop: 10,
    position: 'relative',
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      left: -12,
      right: -12,
      height: 1,
      backgroundColor: theme.palette.active[50],
    },
  },
  iconInfo: {
    display: 'block',
    width: 14,
    height: 14,
    color: theme.palette.grey[600],
  },
  tooltip: {
    margin: '0 0 -3px 4px',
  },
  tooltipSMS: {
    margin: '2px 0 -3px 4px',
  },
  gridItemShrink: {
    flexShrink: 1,
  },
  gridItemAptState: {
    [theme.breakpoints.up('lg')]: {
      maxWidth: '12.5%',
      flexBasis: '12.5%',
    },
  },
  switchLabel: {
    display: 'flex',
  },
  iconSMS: {
    marginRight: 4,
    color: theme.palette.grey[600],
  },
  iconEmail: {
    marginRight: 4,
    marginTop: -2,
    color: theme.palette.grey[600],
  },
  switchField: {
    '& + $switchField': {
      marginLeft: 0,
    },
  },
  iconPrimaryLanguage: {
    width: 16,
    height: 16,
    marginRight: 4,
    marginTop: -2,
    verticalAlign: 'middle',
  },
});

function onSubmit(values, dispatch, props) {
  if (props.pristine) {
    throw new SubmissionError({
      _error: 'Nothing to submit',
    });
  }
  const diffValues = objectDiff(values, props.initialValues);
  const contact = {
    ...diffValues['Primary Phone']
      && { 'Primary Phone': diffValues['Primary Phone'] },
    ...diffValues['Primary Phone'] === ''
      && { 'Primary Phone': null },
    ...diffValues['Home Phone']
      && { 'Home Phone': diffValues['Home Phone'] },
    ...diffValues['Home Phone'] === ''
      && { 'Home Phone': null },
    ...diffValues['Work Phone']
      && { 'Work Phone': diffValues['Work Phone'] },
    ...diffValues['Work Phone'] === ''
      && { 'Work Phone': null },
    ...diffValues['Name Of Emergency Contact']
      && { 'Name Of Emergency Contact': diffValues['Name Of Emergency Contact'] },
    ...diffValues['Name Of Emergency Contact'] === ''
      && { 'Name Of Emergency Contact': null },
    ...diffValues['SMS Notifications'] !== undefined || diffValues['Email Notifications'] !== undefined
      ? {
        ...values['SMS Notifications'] && values['Email Notifications']
          ? { 'Preferred method of communications': 'Both' } : {},
        ...values['SMS Notifications'] && !values['Email Notifications']
          ? { 'Preferred method of communications': 'By SMS' } : {},
        ...!values['SMS Notifications'] && values['Email Notifications']
          ? { 'Preferred method of communications': 'By Email' } : {},
        ...!values['SMS Notifications'] && !values['Email Notifications']
          ? { 'Preferred method of communications': 'None' } : {}, 
      } : {},
    ...diffValues.Email && { Email: diffValues.Email },
    ...diffValues.Email === '' && { Email: null },
  };
  let address = !values['Use Billing Address'] ? {
    ...diffValues.City && { City: diffValues.City },
    ...diffValues.City === '' && { City: null },
    ...diffValues.Street && { Street: diffValues.Street },
    ...diffValues.Street === '' && { Street: null },
    ...diffValues.State && { State: diffValues.State },
    ...diffValues.State === '' && { State: null },
    ...diffValues.Apt && { Apt: diffValues.Apt },
    ...diffValues.Apt === '' && { Apt: null },
    ...diffValues['Zip Code'] && { 'Zip Code': diffValues['Zip Code'] },
    ...diffValues['Zip Code'] === '' && { 'Zip Code': null },
  } : {};
  const billing_address = {
    ...diffValues['Billing City'] && { City: diffValues['Billing City'] },
    ...diffValues['Billing City'] === '' && { City: null },
    ...diffValues['Billing Street'] && { Street: diffValues['Billing Street'] },
    ...diffValues['Billing Street'] === '' && { Street: null },
    ...diffValues['Billing State'] && { State: diffValues['Billing State'] },
    ...diffValues['Billing State'] === '' && { State: null },
    ...diffValues['Billing Apt'] && { Apt: diffValues['Billing Apt'] },
    ...diffValues['Billing Apt'] === '' && { Apt: null },
    ...diffValues['Billing Zip Code'] && { 'Zip Code': diffValues['Billing Zip Code'] },
    ...diffValues['Billing Zip Code'] === '' && { 'Zip Code': null },
  };
  if (diffValues['Use Billing Address']
    || (values['Use Billing Address'] && !values.Street && !values.City && !values.Apt && !values.State && !values['Zip Code'])
    || (values['Use Billing Address'] && Object.keys(billing_address).length > 0)) {
    address =  {
      Street: values['Billing Street'],
      City: values['Billing City'],
      Apt: values['Billing Apt'] === '' ? null : values['Billing Apt'],
      State: values['Billing State'],
      'Zip Code': values['Billing Zip Code'],
    };
  }
  return new Promise((resolve, reject) => {
    if (Object.keys(contact).length === 0
      && Object.keys(address).length === 0
      && Object.keys(billing_address).length === 0
      && !diffValues['Primary Language']
    ) {
      resolve(values);
    } else {
      dispatch(updatePatient({
        values: {
          'Record Number': props['Record Number'],
          ...(Object.keys(contact).length > 0) ? { contact } : {},
          ...(Object.keys(address).length > 0) ? { address } : {},
          ...(Object.keys(billing_address).length > 0) ? { billing_address } : {},
          ...diffValues['Primary Language'] ? { 'Primary Language': diffValues['Primary Language'] } : {},
        },
        resolve,
        reject,
      }));
    }
  }).then(res => res,
    (err) => {
      if (err.error) {
        throw new SubmissionError({ _error: err.error });
      }
      throw new SubmissionError(err);
    });
}

function onChange(values, dispatch, props, prevValues) {
  if (values['Primary Language']
    && primaryLanguages.filter(item => item.name === values['Primary Language Name']).length === 0) {
    dispatch(props.change('Primary Language', ''));
  }
}

const validateForm = (values) => {
  const errors = {};

  if ((values.Street && !values.State) || (values.State && usaStates.arrayOf('abbreviations').indexOf(values.State) === -1)) {
    errors.State = 'Please, select valid state abbreviation';
  }

  if ((values['Billing Street'] && !values['Billing State'])
    || (values['Billing State'] && usaStates.arrayOf('abbreviations').indexOf(values['Billing State']) === -1)) {
    errors['Billing State'] = 'Please, select valid state abbreviation';
  }

  if ((values['Primary Language']
    && primaryLanguages.filter(item => item.name === values['Primary Language Name']).length === 0)
    || !values['Primary Language'] && values['Primary Language Name']) {
    errors['Primary Language Name'] = 'Please, select Language from list';
  }

  return errors;
};

function onSubmitFail(errors) {
  const errorNames = Object.keys(errors);
  if (errorNames.length > 0) {
    const selectorString = errorNames.map(item => `[name="${item}"]`).join(', ');
    const firstErrorEl = document.querySelector(selectorString);
    if (firstErrorEl) {
      const firstErrorElOffsetTop = firstErrorEl.getBoundingClientRect().top;
      const windowScrollTop = window.scrollY !== undefined ? window.scrollY : window.scrollTop;
      const scrollOffset = 50;
      if ((window.innerHeight - scrollOffset) < firstErrorElOffsetTop) {
        const pageHeaderHeight = 64;
        const patientHeaderHeight = 202;
        window.scrollTo(0, (firstErrorElOffsetTop
          + windowScrollTop) - pageHeaderHeight - patientHeaderHeight - scrollOffset);
        firstErrorEl.focus();
      }
    }
  }
}

class ContactInformationEdit extends Component {
  componentDidMount() {
    if (this.props.hash === '#contact-information' && this.addressInformationRef) {
      const offsetTop = this.addressInformationRef.getBoundingClientRect().top;
      const pageHeaderHeight = 64;
      const patientHeaderHeight = 177;
      window.scrollTo(0, offsetTop - pageHeaderHeight - patientHeaderHeight);
    }
  }

  render() {
    const { handleSubmit, change, error, formValues, classes } = this.props;
    return (
      <form
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit}
      >
        {error &&
          <Grid
            container
            spacing={24}
          >
            <Grid
              item
              xs={12}
            >
              <FormError>
                {error}
              </FormError>
            </Grid>
          </Grid>
        }
        <Grid
          container
          justify="flex-start"
          spacing={24}
        >
          <Grid item xs={12} sm={6} md={5} lg={3}>
            <Field
              id="Primary Phone"
              name="Primary Phone"
              label={(
                <div>
                  Cell Phone
                  <Tooltip
                    className={classes.tooltip}
                    content={
                      <Typography variant="subheading" color="secondary">
                        Will be used to message patients
                      </Typography>
                    }
                    color="grey"
                  >
                    <IconInfo className={classes.iconInfo} />
                  </Tooltip>
                </div>
              )}
              component={TextField}
              validate={[validate.required, validate.phone]}
              format={value => (value ? value.replace('+1 ', '') : '')}
              normalize={normalize.phone}
              maxLength="14"
              startAdornment={
                <InputAdornment position="start">
                  <TextFieldAdornmentText>
                    +1
                  </TextFieldAdornmentText>
                </InputAdornment>
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={5} lg={3}>
            <Field
              id="Home Phone"
              name="Home Phone"
              label="Home Phone"
              component={TextField}
              validate={[validate.phone]}
              format={value => (value ? value.replace('+1 ', '') : '')}
              normalize={normalize.phone}
              maxLength="14"
              startAdornment={
                <InputAdornment position="start">
                  <TextFieldAdornmentText>
                    +1
                  </TextFieldAdornmentText>
                </InputAdornment>
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={5} lg={3}>
            <Field
              id="Work Phone"
              name="Work Phone"
              label="Emergency Contact"
              component={TextField}
              validate={[validate.phone]}
              format={value => (value ? value.replace('+1 ', '') : '')}
              normalize={normalize.phone}
              maxLength="14"
              startAdornment={
                <InputAdornment position="start">
                  <TextFieldAdornmentText>
                    +1
                  </TextFieldAdornmentText>
                </InputAdornment>
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={5} lg={3}>
            <Field
              id="Name Of Emergency Contact"
              name="Name Of Emergency Contact"
              label="Emergency Contact Name"
              component={TextField}
              fullWidth
              labelNowrap
            />
          </Grid>
        </Grid>
        <Grid
          container
          justify="flex-start"
          alignItems="flex-end"
          // wrap="nowrap"
          spacing={24}
        >
          <Grid item xs={12} sm={4}>
            <Field
              id="Email"
              name="Email"
              label="Email"
              component={TextField}
              validate={[validate.email]}
              fullWidth
            />
          </Grid>
          <Grid className={classes.gridItemShrink} item xs={6} sm={3}>
            <Field
              id="Primary Language Name"
              name="Primary Language Name"
              label="Primary Language"
              component={AutosuggestField}
              fullWidth
              renderSuggestionContent={(suggestion) => {
                const IconPrimaryLanguage = suggestion.icon;
                return (
                  <Fragment>
                    <IconPrimaryLanguage className={classes.iconPrimaryLanguage} />
                    {suggestion.name}
                  </Fragment>
                );
              }}
              onSuggestionSelected={(suggestion) => {
                change('Primary Language', suggestion.code);
                return suggestion.name;
              }}
              fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
                if (offset === 0) {
                  if (value.length > 0) {
                    resolve(primaryLanguages.filter(item =>
                      item.name.indexOf(value) === 0));
                  } else {
                    resolve(primaryLanguages);
                  }
                } else {
                  resolve([]);
                }
              }))}
              labelNowrap
            />
          </Grid>
          <Grid item>
            <Field
              className={classes.switchField}
              id="SMS Notifications"
              name="SMS Notifications"
              label={(
                <div className={classes.switchLabel}>
                  <IconSMS className={classes.iconSMS} />
                  <div>By SMS</div>        
                  <Tooltip
                    className={classes.tooltipSMS}
                    content={
                      <Typography variant="subheading" color="secondary">
                        I agree to receive messages regarding my order and delivery updates from DME4DME.<br />
                        I understand that data rates may apply, reply UNSUBSCRIBE to opt out.
                      </Typography>
                    }
                    color="grey"
                  >
                    <IconInfo className={classes.iconInfo} />
                  </Tooltip>
                </div>
              )}
              component={SwitchField}
            />
            <Field
              className={classes.switchField}
              id="Email Notifications"
              name="Email Notifications"
              label={(
                <div className={classes.switchLabel}>
                  <IconEmail className={classes.iconEmail} />
                  <div>By Email</div>  
                </div>
              )}
              component={SwitchField}
            />
          </Grid>
        </Grid>
        <RootRef
          rootRef={(addressInformationRef) => {
            this.addressInformationRef = addressInformationRef;
          }}
        >
          <Grid
            className={classes.gridSeparator}
            container
            justify="flex-start"
            wrap="nowrap"
            spacing={24}
          >
            <Grid item xs={12}>
              <Typography variant="subheading">
                Billing Address
                <Tooltip
                  className={classes.tooltip}
                  content={
                    <Typography variant="subheading" color="secondary">
                      This address is going to be sent to the insurance
                    </Typography>
                  }
                  color="grey"
                >
                  <IconInfo className={classes.iconInfo} />
                </Tooltip>
              </Typography>
            </Grid>
          </Grid>
        </RootRef>
        <Grid
          container
          justify="flex-start"
          alignItems="flex-end"
          spacing={24}
        >
          <Grid item xs={9} sm={8} md={8} lg={4}>
            <Field
              id="Billing Street"
              name="Billing Street"
              label="Street"
              component={AutosuggestField}
              validate={[validate.required]}
              placeholder="Lookup Address"
              fullWidth
              renderSuggestionContent={(suggestion) => suggestion.address.label}
              onSuggestionSelected={(suggestion) => {
                if (suggestion.address.city) {
                  change('Billing City', suggestion.address.city);
                }
                if (suggestion.address.postalCode) {
                  change('Billing Zip Code', suggestion.address.postalCode);
                }
                if (suggestion.address.stateCode) {
                  change('Billing State', suggestion.address.stateCode);
                }
                if (suggestion.address.street) {
                  if (suggestion.address.houseNumber) {
                    return `${suggestion.address.houseNumber} ${suggestion.address.street}`;
                  }
                  return suggestion.address.street;
                }
                return suggestion.address.label;
              }}
              fetchSuggestions={({ value }) => (
                value.length > 0
                  ? requestFetchPlacesAutocomplete({
                    q: value,
                    in: 'countryCode:USA',
                    limit: 5,
                  }).then(res => res.items,
                    err => err)
                  : new Promise(resolve => resolve([]))
              )}
            />
          </Grid>
          <Grid className={classes.gridItemAptState} item xs={3} sm={2}>
            <Field
              id="Billing Apt"
              name="Billing Apt"
              label="Apt"
              component={TextField}
              fullWidth
            />
          </Grid>
          <Grid item xs={3} sm={2}>
            <Field
              id="Billing Zip Code"
              name="Billing Zip Code"
              label="Zip Code"
              component={TextField}
              validate={[validate.required, validate.zip]}
              normalize={normalize.zip}
              maxLength="5"
              fullWidth
            />
          </Grid>
          <Grid item xs={6} sm={4} md={4} lg={3}>
            <Field
              id="Billing City"
              name="Billing City"
              label="City"
              component={TextField}
              validate={[validate.required]}
              fullWidth
            />
          </Grid>
          <Grid
            className={classes.gridItemAptState}
            item
            xs={3}
            sm={2}
          >
            <Field
              id="Billing State"
              name="Billing State"
              label="State"
              component={AutosuggestField}
              normalize={normalize.uppercase}
              fullWidth
              renderSuggestionContent={suggestion => suggestion.abbreviation}
              onSuggestionSelected={suggestion => suggestion.abbreviation}
              fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
                if (offset === 0) {
                  if (value.length > 0) {
                    resolve(usaStates.states.filter(item =>
                      item.abbreviation.indexOf(value) === 0));
                  } else {
                    resolve(usaStates.states);
                  }
                } else {
                  resolve([]);
                }
              }))}
            />
          </Grid>
        </Grid>
        <Grid
          className={classes.grid}
          container
          justify="flex-start"
          alignItems="center"
          wrap="nowrap"
          spacing={24}
        >
          <Grid item>
            <Typography variant="subheading">
              Delivery Address
              <Tooltip
                className={classes.tooltip}
                content={
                  <Typography variant="subheading" color="secondary">
                    This address will be used on delivery instructions
                  </Typography>
                }
                color="grey"
              >
                <IconInfo className={classes.iconInfo} />
              </Tooltip>
            </Typography>
          </Grid>
          <Grid item>
            <Field
              id="Use Billing Address"
              name="Use Billing Address"
              label="Use Billing Address"
              component={CheckboxField}
            />
          </Grid>
        </Grid>
        {formValues && !formValues['Use Billing Address'] && (
          <Grid
            className={classes.grid}
            container
            justify="flex-start"
            alignItems="flex-end"
            spacing={24}
          >
            <Grid item xs={9} sm={8} md={8} lg={4}>
              <Field
                id="Street"
                name="Street"
                label="Street"
                component={AutosuggestField}
                validate={[validate.required]}
                placeholder="Lookup Address"
                fullWidth
                renderSuggestionContent={(suggestion) => suggestion.address.label}
                onSuggestionSelected={(suggestion) => {
                  if (suggestion.address.city) {
                    change('City', suggestion.address.city);
                  }
                  if (suggestion.address.postalCode) {
                    change('Zip Code', suggestion.address.postalCode);
                  }
                  if (suggestion.address.stateCode) {
                    change('State', suggestion.address.stateCode);
                  }
                  if (suggestion.address.street) {
                    if (suggestion.address.houseNumber) {
                      return `${suggestion.address.houseNumber} ${suggestion.address.street}`;
                    }
                    return suggestion.address.street;
                  }
                  return suggestion.address.label;
                }}
                fetchSuggestions={({ value }) => (
                  value.length > 0
                    ? requestFetchPlacesAutocomplete({
                      q: value,
                      in: 'countryCode:USA',
                      limit: 5,
                    }).then(res => res.items,
                      err => err)
                    : new Promise(resolve => resolve([]))
                )}
              />
            </Grid>
            <Grid className={classes.gridItemAptState} item xs={3} sm={2}>
              <Field
                id="Apt"
                name="Apt"
                label="Apt"
                component={TextField}
                fullWidth
              />
            </Grid>
            <Grid item xs={3} sm={2}>
              <Field
                id="Zip Code"
                name="Zip Code"
                label="Zip Code"
                component={TextField}
                validate={[validate.required, validate.zip]}
                normalize={normalize.zip}
                maxLength="5"
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={4} md={4} lg={3}>
              <Field
                id="City"
                name="City"
                label="City"
                component={TextField}
                validate={[validate.required]}
                fullWidth
              />
            </Grid>
            <Grid
              className={classes.gridItemAptState}
              item
              xs={3}
              sm={2}
            >
              <Field
                id="State"
                name="State"
                label="State"
                component={AutosuggestField}
                normalize={normalize.uppercase}
                fullWidth
                renderSuggestionContent={suggestion => suggestion.abbreviation}
                onSuggestionSelected={suggestion => suggestion.abbreviation}
                fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
                  if (offset === 0) {
                    if (value.length > 0) {
                      resolve(usaStates.states.filter(item =>
                        item.abbreviation.indexOf(value) === 0));
                    } else {
                      resolve(usaStates.states);
                    }
                  } else {
                    resolve([]);
                  }
                }))}
              />
            </Grid>
          </Grid>
        )}
      </form>
    );
  }
}

ContactInformationEdit.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  error: PropTypes.string,
  hash: PropTypes.string,
  classes: PropTypes.shape({
    gridSeparator: PropTypes.string.isRequired,
  }).isRequired,
};

ContactInformationEdit.defaultProps = {
  error: null,
  hash: null,
};

export default reduxForm({
  enableReinitialize: true,
  onSubmit,
  onSubmitFail,
  onChange,
  validate: validateForm,
  initialValues: {
    'Use Billing Address': true,
    'Preferred method of communications': 'Both',
  },
})(withStyles(styles)(ContactInformationEdit));



// WEBPACK FOOTER //
// ./src/components/patient/PatientProfile/ContactInformation/ContactInformationEdit.js